// src/components/PlanPicker.js
//
// Shared yearly/monthly plan UI — ONE definition used by /join, /class/:id
// (inline cards) and Workouts (the "Choose your membership" sheet), so the
// surfaces can never describe the plans differently.
//
//   useAnnualOffer(enabled)  → offer | null   (null ⇒ caller shows today's UI)
//   <PlanOptions/>           → the two selectable cards
//   <PlanPickerModal/>       → cards + Continue, for in-app subscribe buttons
//
// All sub-components are module scope; hover lives in useState (never inline
// DOM mutation). Soft text tones are derived with color-mix from charcoal,
// because production sets --mom-warm-gray identical to --mom-charcoal.
// See docs/ux/annual-plan.md.

import React, { useEffect, useState } from 'react';
import {
    buildAnnualOffer, fetchSubscriptionPlans, fillTemplate, getCachedPlans,
} from '../services/subscriptionPlans';

const MUTED = 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))';
const HAIRLINE = 'color-mix(in srgb, var(--mom-charcoal) 16%, transparent)';

export function useAnnualOffer(enabled) {
    const [plans, setPlans] = useState(() => (enabled ? getCachedPlans() : null));
    useEffect(() => {
        if (!enabled) return undefined;
        let live = true;
        setPlans((prev) => prev || getCachedPlans());
        fetchSubscriptionPlans().then((p) => {
            // A failed fetch (null) keeps whatever we had; a successful response
            // replaces it — including one that says yearly is unavailable.
            if (live && p) setPlans(p);
        });
        return () => { live = false; };
    }, [enabled]);
    return enabled ? buildAnnualOffer(plans) : null;
}

function RadioDot({ selected, dark }) {
    const ring = dark ? '#fff' : 'var(--mom-primary)';
    return (
        <span aria-hidden="true" style={{
            width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
            border: `1.5px solid ${selected ? ring : (dark ? 'rgba(255,255,255,0.6)' : HAIRLINE)}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            transition: 'border-color 0.15s',
        }}>
            {selected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: ring }} />}
        </span>
    );
}

function PlanCard({ selected, dark, label, price, note, badge, onSelect }) {
    const [hover, setHover] = useState(false);
    const border = dark
        ? (selected ? '1.5px solid #fff' : '1.5px solid rgba(255,255,255,0.4)')
        : (selected ? '1.5px solid var(--mom-primary)' : `1.5px solid ${HAIRLINE}`);
    const background = dark
        ? (selected ? 'rgba(255,255,255,0.22)' : (hover ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)'))
        : (selected ? 'color-mix(in srgb, var(--mom-primary) 7%, var(--mom-white, #fff))' : (hover ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.65)'));
    const text = dark ? '#fff' : 'var(--mom-charcoal)';
    const sub = dark ? 'rgba(255,255,255,0.82)' : MUTED;

    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                textAlign: 'left', cursor: 'pointer', width: '100%',
                padding: '14px 16px', borderRadius: 14, border, background,
                boxShadow: selected && !dark ? '0 4px 14px color-mix(in srgb, var(--mom-primary) 14%, transparent)' : 'none',
                transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
                fontFamily: 'var(--mom-font-sans)',
            }}
        >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RadioDot selected={selected} dark={dark} />
                <span style={{ fontSize: 14, fontWeight: 700, color: text }}>{label}</span>
                {badge && (
                    <span style={{
                        marginLeft: 'auto', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
                        padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                        color: dark ? 'var(--mom-primary-dark)' : 'var(--mom-primary-dark)',
                        background: dark ? '#fff' : 'color-mix(in srgb, var(--mom-primary) 13%, transparent)',
                    }}>
                        {badge}
                    </span>
                )}
            </span>
            <span style={{ display: 'block', margin: '6px 0 2px 24px', fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 21, color: text, lineHeight: 1.2 }}>
                {price}
            </span>
            <span style={{ display: 'block', marginLeft: 24, fontSize: 12, color: sub, lineHeight: 1.4 }}>
                {note}
            </span>
        </button>
    );
}

export function PlanOptions({ offer, copy, value, onChange, tone = 'light', maxWidth = 460 }) {
    if (!offer) return null;
    const dark = tone === 'dark';
    return (
        <div role="radiogroup" aria-label={copy.pickerHeading} style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10, width: '100%', maxWidth,
        }}>
            <PlanCard
                dark={dark}
                selected={value === 'annual'}
                onSelect={() => onChange('annual')}
                label={copy.yearlyLabel}
                price={offer.annualPrice}
                note={fillTemplate(copy.yearlyNote, { price: offer.annualPlain })}
                badge={fillTemplate(copy.savingsTemplate, { amount: offer.savingsPlain })}
            />
            <PlanCard
                dark={dark}
                selected={value === 'monthly'}
                onSelect={() => onChange('monthly')}
                label={copy.monthlyLabel}
                price={offer.monthlyPrice}
                note={fillTemplate(copy.monthlyNote, { price: offer.monthlyPlain })}
            />
        </div>
    );
}

export function PlanPickerModal({ open, offer, copy, loading, onClose, onContinue }) {
    const [plan, setPlan] = useState('annual');
    // Every opening starts on the pre-selected yearly plan.
    useEffect(() => { if (open) setPlan('annual'); }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, loading, onClose]);

    if (!open || !offer) return null;

    return (
        <div
            onClick={() => { if (!loading) onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 1050,
                background: 'rgba(30,20,20,0.45)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={copy.pickerHeading}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 460, borderRadius: 24, padding: '26px 22px 22px',
                    background: 'var(--mom-cream)', boxShadow: 'var(--shadow-xl, 0 24px 60px rgba(0,0,0,0.2))',
                    position: 'relative', animation: 'modalIn 0.25s ease',
                }}
            >
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    disabled={loading}
                    style={{
                        position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: '50%',
                        border: 'none', background: 'transparent', cursor: 'pointer', color: MUTED, fontSize: 20, lineHeight: 1,
                    }}
                >
                    ×
                </button>
                <h2 style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 24,
                    color: 'var(--mom-charcoal)', margin: '0 0 18px', paddingRight: 28,
                }}>
                    {copy.pickerHeading}
                </h2>
                <PlanOptions offer={offer} copy={copy} value={plan} onChange={setPlan} maxWidth="100%" />
                <button
                    type="button"
                    onClick={() => onContinue(plan)}
                    disabled={loading}
                    style={{
                        display: 'block', width: '100%', marginTop: 18, padding: '15px 20px', borderRadius: 14,
                        border: 'none', cursor: loading ? 'default' : 'pointer',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 700, color: '#fff',
                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                        boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 24%, transparent)',
                        opacity: loading ? 0.6 : 1,
                    }}
                >
                    {loading ? 'Redirecting…' : copy.continueLabel}
                </button>
            </div>
        </div>
    );
}
